// src/components/FolderTree.tsx
import React, { useMemo } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Tooltip,
  Stack,
  Box,
} from "@mui/material";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import DescriptionIcon from "@mui/icons-material/Description";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type { FolderRow } from "../types/folder";

interface FolderTreeProps {
  folders: FolderRow[];
  loading: boolean;
  error: string | null;
  selectedFolderId: string | null;
  onSelectFolder: (folder: FolderRow) => void;
  onClickCreateChild: (parent: FolderRow) => void;
  onRequestDeleteFolder: (folder: FolderRow) => void;
  readOnly?: boolean;
}

interface FolderNode extends FolderRow {
  children: FolderNode[];
  // ✅ 형제 중 파일 보유자가 있어서 나도 덩달아 'Leaf'로 강제되는지 여부
  isLeafForced: boolean;
}

const buildTree = (folders: FolderRow[]): FolderNode[] => {
  const map = new Map<string, FolderNode>();
  const roots: FolderNode[] = [];

  // 1. 노드 초기화
  folders.forEach((f) => {
    map.set(f.folder_id, { ...f, children: [], isLeafForced: false });
  });

  // 2. 트리 구성 및 '형제 파일 보유 여부' 체크를 위한 그룹핑
  const groups = new Map<string | null, FolderNode[]>(); // parentId -> children

  map.forEach((node) => {
    const pid = node.parent_folder_id ?? null;
    if (!groups.has(pid)) groups.set(pid, []);
    groups.get(pid)!.push(node);

    if (pid === null) {
      roots.push(node);
    } else {
      const parent = map.get(pid);
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node); // 고아 노드 처리
      }
    }
  });

  // 3. [핵심] 형제들 중 하나라도 파일이 있으면, 해당 그룹 전체를 Leaf로 강제
  groups.forEach((siblings) => {
    const hasFileSibling = siblings.some((s) => s.file_count > 0);
    if (hasFileSibling) {
      siblings.forEach((s) => {
        s.isLeafForced = true;
      });
    }
  });

  // 4. 정렬
  const sortRecursive = (nodes: FolderNode[]) => {
    nodes.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
    );
    nodes.forEach((n) => sortRecursive(n.children));
  };
  sortRecursive(roots);

  return roots;
};

const FolderTree: React.FC<FolderTreeProps> = ({
  folders,
  loading,
  error,
  selectedFolderId,
  onSelectFolder,
  onClickCreateChild,
  onRequestDeleteFolder,
  readOnly = false,
}) => {
  const tree = useMemo(() => buildTree(folders), [folders]);

  const renderNode = (node: FolderNode, depth: number) => {
    const hasFiles = node.file_count > 0;
    const isSelected = selectedFolderId === node.folder_id;
    
    // 파일이 있으면 꽉 찬 아이콘, 없으면 열린 폴더
    const IconComp = hasFiles ? FolderZipIcon : FolderOpenIcon;

    // ✅ + 버튼 숨김 조건:
    // 1. 내 안에 파일이 있다 (hasFiles) -> 당연히 못 만듦
    // 2. 내 형제 중에 파일이 있는 놈이 있다 (isLeafForced) -> 나도 덩달아 못 만듦 (단, 루트가 아닐 때 등 정책에 따라 다르지만 사장님 컨셉상 형제 레벨 통일)
    const canCreateSubFolder = !hasFiles && !node.isLeafForced;

    return (
      <React.Fragment key={node.folder_id}>
        <ListItem
          disablePadding
          sx={{
            pl: 1 + depth * 2,
            borderLeft: isSelected ? '4px solid #1976d2' : '4px solid transparent',
            bgcolor: isSelected ? 'action.selected' : 'inherit',
          }}
        >
          <ListItemButton
            selected={isSelected}
            onClick={() => onSelectFolder(node)}
            sx={{ pr: readOnly ? 2 : 12 }}
          >
            <IconComp 
              fontSize="small" 
              sx={{ 
                mr: 1, 
                color: hasFiles ? 'secondary.main' : (isSelected ? 'primary.main' : 'text.secondary') 
              }} 
            />
            <ListItemText
              primary={node.name}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: isSelected ? 700 : 400,
                color: isSelected ? 'primary.main' : 'text.primary',
                noWrap: true,
              }}
            />
          </ListItemButton>

          {!readOnly && (
            <ListItemSecondaryAction>
              <Stack direction="row" spacing={0}>
                {/* 하위 폴더 생성 버튼 제어 */}
                {canCreateSubFolder && (
                  <Tooltip title="하위 폴더 생성">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickCreateChild(node);
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="폴더 삭제">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestDeleteFolder(node);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </ListItemSecondaryAction>
          )}
        </ListItem>
        <Divider component="li" />
        {node.children.map((child) => renderNode(child, depth + 1))}
      </React.Fragment>
    );
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} /></Box>;
  if (error) return <Alert severity="error" sx={{ mx: 2, my: 1 }}>{error}</Alert>;
  if (!loading && folders.length === 0) return null;

  return (
    <List dense sx={{ width: '100%', bgcolor: 'background.paper', p: 0 }}>
      {tree.map((root) => renderNode(root, 0))}
    </List>
  );
};

export default FolderTree;